export interface DeviceSwitch {
    /** Label of the preferred device that went away, when we saw it before. */
    readonly lost?: string;
    /** Label of the system default that took over, when the browser names it. */
    readonly now?: string;
}

type Device = Pick<MediaDeviceInfo, 'deviceId' | 'kind' | 'label'>;

// Chrome names the default entry "Default - <device>".
const plainLabel = (label: string): string => label.replace(/^default\s*-\s*/i, '').trim();

/**
 * D-CONF-04/S03: a preferred microphone or speaker went away, so the call fell back to the
 * system default. Undefined when nothing we use disappeared.
 */
export const deviceSwitchOf = (before: readonly Device[], after: readonly Device[], wanted: readonly string[]): DeviceSwitch | undefined => {
    const present = new Set(after.map(device => device.deviceId));
    const goneId = wanted.find(id => !present.has(id));
    if (!goneId) {
        return undefined;
    }
    const gone = before.find(device => device.deviceId === goneId);
    const fallback = after.find(device => device.deviceId === 'default' && device.kind === (gone?.kind ?? 'audioinput'))
        ?? after.find(device => device.kind === (gone?.kind ?? 'audioinput'));
    return {
        lost: gone?.label ? plainLabel(gone.label) : undefined,
        now: fallback?.label ? plainLabel(fallback.label) : undefined,
    };
};
